import { Check, Copy, KeyRound } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export interface OneTimePassword {
  name: string;
  email: string;
  temporaryPassword: string;
}

/**
 * Shows a temporary password exactly once. Nothing stores it: closing the dialog discards it,
 * and the backend only keeps a hash.
 */
export function TemporaryPasswordDialog({
  credentials,
  title,
  note,
  onClose,
}: {
  credentials: OneTimePassword | null;
  title: string;
  /** What happens when they sign in (differs between staff and dealer accounts). */
  note: ReactNode;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset "Copied" for each new password
  useEffect(() => setCopied(false), [credentials]);
  if (!credentials) return null;
  const copy = () =>
    void navigator.clipboard?.writeText(credentials.temporaryPassword).then(() => {
      setCopied(true);
      toast.success("Temporary password copied");
    });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="size-4" aria-hidden />
            {title}
          </DialogTitle>
          <DialogDescription>
            Temporary password for <span className="font-medium text-foreground">{credentials.name}</span> ({credentials.email}).
          </DialogDescription>
        </DialogHeader>
        <div className="my-3 space-y-3">
          <div className="flex items-center gap-2 rounded-md border bg-muted/50 p-2">
            <output className="min-w-0 flex-1 select-all break-all font-mono text-sm" aria-label="Temporary password">
              {credentials.temporaryPassword}
            </output>
            <Button size="sm" variant="outline" onClick={copy}>
              {copied ? <Check data-icon="inline-start" /> : <Copy data-icon="inline-start" />}
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
          <Alert>
            <AlertDescription className="text-xs">
              This password is shown only once. Share it through a secure channel (not email or chat history). {note}
            </AlertDescription>
          </Alert>
        </div>
        <DialogFooter>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
